import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TixAnalyticsService } from '@tix/shared/analytics';
import { Observable } from 'rxjs';
import {
  getSelectedEventsAggregate,
  TixCompanyPartialState
} from '@tix/company/state';
import * as CompanySelectors from '@tix/company/state/selectors';
import * as EventsSelectors from '@tix/events/state';
import * as CustomersService from '@tix/customer/components';
import { Store } from '@ngrx/store';
import { map, mergeMap } from 'rxjs/operators';
import { TixStaffService } from '@tix/staff/state';
import { Roles } from '@tix/auth/state';
import { TooltipPosition } from '@angular/material/tooltip';
import { ReleaseSwitcherService } from '@tix/auth/services';

@Component({
  selector: 'tix-company-sidebar',
  templateUrl: './company-sidebar.component.html',
  styleUrls: ['./company-sidebar.component.scss'],
  host: {
    class: 'tix-company-sidebar'
  }
})
export class TixCompanySidebarComponent implements OnInit {
  readonly companyId$: Observable<string | undefined> = this.store.select(
    CompanySelectors.getSelectedCompanyId
  );
  readonly venuesAggregate$: Observable<number | undefined> = this.store.select(
    CompanySelectors.getSelectedVenuesAggregate
  );
  readonly contactsAggregate$: Observable<number | undefined> =
    this.store.select(CompanySelectors.getSelectedContactsAggregate);
  readonly eventsAggregate$: Observable<number | undefined> = this.store.select(
    CompanySelectors.getSelectedEventsAggregate
  );
  readonly eventsLiveAggregate$: Observable<number | undefined> = this.store
    .select(EventsSelectors.getAllEvents)
    .pipe(map(events => events.length));

  readonly customersAggregate$: Observable<number | undefined> =
    this.companyId$.pipe(
      mergeMap(companyId =>
        this.customerService.getCustomerCount(companyId as string)
      )
    );

  userRolesInCompany$ = this.companyId$.pipe(
    mergeMap(companyId =>
      this.staffService.getCurrentUserRolesInCompany(companyId as string)
    )
  );

  @Input() expandSidebar = true;
  @Output() expandSidebarChanged = new EventEmitter<boolean>();
  @Output() closeMobileSidebar = new EventEmitter<void>();

  matTooltipPosition: TooltipPosition = 'right';

  constructor(
    private readonly store: Store<TixCompanyPartialState>,
    private staffService: TixStaffService,
    private customerService: CustomersService.TixCustomerService,
    private analytics: TixAnalyticsService,
    private releaseSwitcherService: ReleaseSwitcherService
  ) {}

  isBoxOfficeUser(roles: Roles[]) {
    return roles.includes(Roles.BO_USER);
  }

  isManager(roles: Roles[]) {
    return roles.includes(Roles.MANAGER);
  }

  isAdmin(roles: Roles[]) {
    return roles.includes(Roles.CO_ADMIN);
  }
  isEventAdministrator(roles: Roles[]) {
    return roles.includes(Roles.EVENT_ADMINISTRATOR);
  }

  ngOnInit(): void {}

  toggleSidebar(): void {
    if (this.isMobileView()) {
      // On mobile, close the sidebar completely
      this.closeMobileSidebar.emit();
    } else {
      // On desktop, toggle between expanded/collapsed
      this.expandSidebarChanged.emit(!this.expandSidebar);
    }
  }

  onNavigationClick(): void {
    if (this.isMobileView()) {
      // Close mobile sidebar when navigating
      this.closeMobileSidebar.emit();
    }
  }

  private isMobileView(): boolean {
    return window.innerWidth < 1024; // lg breakpoint in Tailwind
  }

  switchToStableRelease(): void {
    const sourceRelease = this.releaseSwitcherService.getSourceRelease();
    this.analytics.captureEvent('new_version_opt_out', {
      action: 'switch_to_old_version',
      source: 'company_sidebar',
      target_release: sourceRelease
    });
    this.releaseSwitcherService.switchToRelease(sourceRelease as any);
  }
}
